import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Navbar from '../navbar';
import Footer from '../footer';
import { useAppSelector } from '@/shared/hooks/store';
import { selectUserProfileInfo, userDetails } from '@/store/slices/onboarding/selectors';
import { selectIsAuthorized } from '@/store/slices/auth/selectors';
import { CustomersStepTypeEnumPaths } from '@/shared/endpoints/customers';
import { useParams, ScrollRestoration, useLocation, useNavigate } from 'react-router-dom';
import { onboardBackground } from '../../helpers';
import { HeaderHeightContext } from '../../../../shared/components/header-height-context';
import IntercomAccessibe from '@/shared/components/IntercomAccessiBe';
import { checkEligibilityAndSave } from '@/shared/helpers/eligibility';
import { ABSOLUTE_ROUTES } from '@/shared/constants/routes';
import { useDispatch } from 'react-redux';
import { setEligibilityFailedModalOpen } from '@/store/slices/onboarding';
import EligibilityFailedModal from '@/shared/components/eligibility-failed-modal';


export interface LayoutProps {
    children: ReactNode;
    spacings?: boolean;
    hasFooter?: boolean;
    extraClasses?: Array<string>;
    paddings?: boolean;
    stepComponentClassPrefix?: string;
    ivoryBg?: boolean;
    withDropdown?: boolean;
    onboardingPassed?: boolean;
    step: string;
}

const Layout = ({
    children,
    spacings = true,
    paddings = false,
    hasFooter = true,
    extraClasses = [],
    stepComponentClassPrefix = '',
    ivoryBg = false,
    withDropdown = true,
    onboardingPassed = false,
    step
}: LayoutProps) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useAppSelector(userDetails);
    const userProfile = useAppSelector(selectUserProfileInfo);
    const isLoggedIn = useAppSelector(selectIsAuthorized);
    const { pathname } = useLocation();
    const { stepType } = useParams();

    const background = useMemo(
        () => onboardBackground(!!isLoggedIn, stepType as CustomersStepTypeEnumPaths.ABOUT_YOU),
        [isLoggedIn, stepType]
    );

    useEffect(() => {
        setTimeout(() => window.scrollTo(0, 0));
    }, [pathname]);

    useEffect(() => {
        if (headerRef.current) {
            setHeaderHeight(headerRef?.current?.getBoundingClientRect()?.height || 0);
        }

        (async () => {
            if (!onboardingPassed) {
                const eligibilityPassed = await checkEligibilityAndSave();
                if (eligibilityPassed === false) {
                    navigate(ABSOLUTE_ROUTES.ONBOARDING.INSURANCE.MAIN);
                    dispatch(setEligibilityFailedModalOpen(true));
                }
            }
        })();
    }, [navigate, dispatch, headerRef, onboardingPassed]);


    const isLoggedInAdditionalStyles = useMemo(() => {
        return isLoggedIn ? {
            paddingTop: `${headerHeight}px
        ` }
            : {};
    }, [headerHeight, isLoggedIn]);

    return (
        <HeaderHeightContext.Provider value={headerHeight}>
            <ScrollRestoration />
            <div
                className={`flex min-h-screen flex-col overflow-hidden ${extraClasses.join(' ')} ${stepComponentClassPrefix ? stepComponentClassPrefix + '__layout' : ''}`}
                style={{
                    background
                }}
            >
                <Navbar
                    headerRef={headerRef}
                    user={user || undefined}
                    userProfile={userProfile}
                    withDropdown={withDropdown}
                    step={step}
                    isLoggedIn={isLoggedIn}
                />
                <div
                    style={{ ...isLoggedInAdditionalStyles }}
                    className={`relative flex h-full flex-auto flex-col  ${paddings ? 'px-5 md:px-12 lg:px-4 xl:px-24' : 'px-0'}`}
                >
                    {children}
                </div>
                {hasFooter && <Footer ivoryBg={ivoryBg} />}
                <IntercomAccessibe />
            </div>
            <EligibilityFailedModal />
        </HeaderHeightContext.Provider>
    );
};

export default Layout;
//${spacings ? 'pt-14 sm:pt-20 md:pt-[92px]' : ''}